import {
  Stack,
  Box,
  Typography,
  Collapse,
  Chip,
  SxProps,
  Tooltip,
} from "@mui/material";
import { useRef, useState } from "react";
import TextButton from "@/components/common/buttons/textButton";
import {
  CheckIcon,
  CartIcon,
  MinusCircleIcon,
} from "@/components/common/icons";
import { MoxieImage } from "@/components/common/images/moxieImage";
import { VIOLET, GREY } from "@/config/mui/colorPalette";
import { useTextOverflowConditionalTooltip } from "@/hooks/misc/useTextOverflowConditionalTooltip";
import { PackageDrawerItem } from "@/hooks/payments/paymentDrawer/useInvoiceManagement";
import { formattedPrice } from "@/utils";
import PackageItemsList from "./paymentDrawerPackageItemsList";

type PackageCardProps = {
  packageItem: PackageDrawerItem;
  isSelected: boolean;
  onSelect: () => void;
};

const PackageSuggestionCard = ({
  packageItem,
  isSelected,
  onSelect,
}: PackageCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const textRef = useRef<HTMLDivElement>(null);
  const thumbnailUrl =
    packageItem.additionalInfo?.thumbnail ?? "/assets/images/gift.png";

  const tooltipProps = useTextOverflowConditionalTooltip(textRef);

  return (
    <Stack
      direction="row"
      spacing={1.5}
      onClick={onSelect}
      sx={{
        width: "100%",
        backgroundColor: isSelected ? VIOLET[20] : "transparent",
        borderRadius: "8px",
        padding: 1,
        cursor: "pointer",
        transition: "background-color 0.2s",
        "&:hover": {
          backgroundColor: isSelected ? VIOLET[20] : GREY[10],
          "& .icon-container": {
            "& .default-icon": {
              display: isSelected ? "none" : "flex",
            },
            "& .hover-icon": {
              display: isSelected ? "flex" : "none",
            },
          },
        },
        "& .icon-container": {
          "& .default-icon": {
            display: "flex",
          },
          "& .hover-icon": {
            display: "none",
          },
        },
      }}
    >
      <PackageImage thumbnailUrl={thumbnailUrl} />
      <Box sx={{ flex: 1 }}>
        <Box
          sx={{
            marginBottom: 1,
            maxWidth: {
              xs: "230px",
              sm: "400px",
            },
          }}
        >
          <Tooltip
            title={packageItem.packageName}
            {...tooltipProps}
            arrow
            enterTouchDelay={1500}
            leaveTouchDelay={500}
            slotProps={{
              popper: {
                sx: {
                  userSelect: "none",
                },
              },
            }}
          >
            <Typography
              ref={textRef}
              variant="subtitleDefault"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {packageItem.packageName}
            </Typography>
          </Tooltip>
        </Box>
        <Stack
          sx={{
            gap: 1,
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <Typography variant="labelSmall">
            {formattedPrice(packageItem.packagePrice)}
          </Typography>
          {packageItem.packageSavings && (
            <PackageChip savings={packageItem.packageSavings} />
          )}
        </Stack>
        <Collapse in={isExpanded}>
          <PackageItemsList items={packageItem.packageItems} />
        </Collapse>
        <TextButton
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
        >
          {isExpanded ? "Hide perks" : "Show perks"}
        </TextButton>
      </Box>
      <CardIcon isSelected={isSelected} />
    </Stack>
  );
};

const PackageImage = ({ thumbnailUrl }: { thumbnailUrl: string }) => {
  return (
    <Box
      sx={{
        width: 48,
        height: 48,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "12px",
        backgroundColor: GREY[20],
      }}
    >
      <MoxieImage
        imageUrl={thumbnailUrl}
        alt="Package thumbnail"
        style={{
          borderRadius: "8px",
        }}
      />
    </Box>
  );
};

const CardIcon = ({ isSelected }: { isSelected: boolean }) => {
  return (
    <Box
      className="icon-container"
      sx={{
        display: "flex",
        alignItems: "center",
        width: "32px",
        height: "32px",
        backgroundColor: isSelected ? VIOLET[40] : "transparent",
        border: isSelected ? "none" : `1px solid ${GREY[20]}`,
        borderRadius: "50%",
        justifyContent: "center",
      }}
    >
      {isSelected ? (
        <>
          <Box className="default-icon">
            <CheckIcon size={16} />
          </Box>
          <Box className="hover-icon">
            <MinusCircleIcon />
          </Box>
        </>
      ) : (
        <Box className="default-icon">
          <CartIcon color={GREY[70]} size={18} />
        </Box>
      )}
    </Box>
  );
};

export const PackageChip = ({
  savings,
  sx,
}: {
  savings: string;
  sx?: SxProps;
}) => (
  <Chip
    label={
      <Typography variant="labelTiny">
        {formattedPrice(savings)} Savings
      </Typography>
    }
    size="small"
    sx={{
      height: "20px",
      backgroundColor: VIOLET[40],
      "& .MuiChip-label": {
        fontSize: "12px",
        fontWeight: 500,
        color: VIOLET[100],
        px: 1,
      },
      ...sx,
    }}
  />
);

export default PackageSuggestionCard;
